import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import CssBaseline from '@mui/material/CssBaseline'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import { FC, useState, useTransition } from 'react'
import toast from 'react-hot-toast'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import Copyright from '../components/common/Copyright'
import FormActivation, { IFormActivationState } from '../components/oauth/FormActivation'
import FormSignIn, { IFormSignInState } from '../components/oauth/FormSignIn'
import SignUpForm, { ISignUpFormState } from '../components/oauth/SignUpForm'
import { useAuth } from '../hooks/useAuth'
import { useModal } from '../hooks/useModal'
import { getMsg } from '../messages/infoMessages'

export interface IOauthState {
  signIn: IFormSignInState
  signUp: ISignUpFormState
  remind?: {}
  activate: IFormActivationState
}
export interface IOauthErrState {
  signIn: Partial<IOauthState['signIn']>
  signUp: Partial<IOauthState['signUp']>
  remind?: {}
  activate?: Partial<IOauthState['activate']>
}
type TOauthFlow = 'signIn' | 'signUp' | 'remind' | 'activate'

const OauthPage: FC<{ flow?: TOauthFlow; originalUrl?: string }> = ({ flow = 'signIn', originalUrl = '/imports' }) => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const modal = useModal()
  const { handleSignIn, handleSignUp, handleActivate } = useAuth()
  const [isPending, startTransition] = useTransition()

  const [oauthErrState, setOauthErrState] = useState<Partial<IOauthErrState>>()
  const [oauthState, setOauthState] = useState<IOauthState>({
    signIn: {
      email: '',
      password: '',
    },
    signUp: {
      email: '',
      firstName: '',
      password: '',
      passwordConfirm: '',
    },
    activate: {
      activationKey: searchParams.get('activationKey') ?? '',
    },
  })
  const [isRedirect, setRedirect] = useState<boolean>(false)

  function setErrPart(flow: TOauthFlow, errFlowState?: IOauthState[typeof flow]) {
    return setOauthErrState((prevState) => ({
      ...prevState,
      [flow]: errFlowState,
    }))
  }

  const onHandler = (flow: TOauthFlow) => async () => {
    // -- Cleaning flow erros
    setErrPart(flow)
    // -- Invoking flow handler
    const flowErrs = await {
      signIn: handleSignIn,
      signUp: handleSignUp,
      remind: handleSignIn,
      activate: handleActivate,
    }[flow](oauthState)
    // -- Proccessing the result
    flowErrs
      ? setErrPart(flow, flowErrs)
      : {
          signIn: () => {
            startTransition(() => {
              setRedirect(() => true)
            })
          },
          signUp: () => {
            toast.success(`${getMsg('signedUp')}`, { duration: 8000 })
            modal.open({
              body: (
                <Alert severity='success'>
                  <AlertTitle>Success Registration</AlertTitle>
                  You have successfully registered.
                  <br />
                  We sent a verifacation code to your email.
                </Alert>
              ),
            })
            startTransition(() => {
              navigate('/oauth/signIn')
            })
          },
          remind: () => {},
          activate: () => {
            toast.success(`${getMsg('activated-user')}`, { duration: 8000 })
            modal.open({
              body: (
                <Alert severity='success'>
                  <AlertTitle>Successful Activation</AlertTitle>
                  Your email havs successfully activated.
                  <br />
                  Now, you can Sign In.
                </Alert>
              ),
            })
            startTransition(() => {
              navigate('/oauth/signIn')
            })
          },
        }[flow]()
  }

  return isRedirect ? (
    <Navigate to={originalUrl} replace />
  ) : (
    <Grid container component='main' sx={{ height: '100vh' }}>
      <CssBaseline />
      <Grid
        item
        xs={false}
        sm={4}
        md={7}
        sx={{
          backgroundImage: 'url(https://source.unsplash.com/random?wallpapers)',
          backgroundRepeat: 'no-repeat',
          backgroundColor: (t) => (t.palette.mode === 'light' ? t.palette.grey[50] : t.palette.grey[900]),
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      <Grid item xs={12} sm={8} md={5} component={Paper} elevation={6} square>
        <Box
          sx={{
            my: 8,
            mx: 'auto',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            maxWidth: 365,
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
            <LockOutlinedIcon />
          </Avatar>

          {(flow === 'signIn' && (
            <FormSignIn
              state={oauthState.signIn}
              stateErr={oauthErrState?.signIn || {}}
              onHandler={onHandler(flow)}
              setState={(key: keyof IOauthState['signIn'], value: string) =>
                setOauthState((prevState) => ({
                  ...prevState,
                  [flow]: {
                    ...prevState[flow],
                    [key]: value,
                  },
                }))
              }
            />
          )) ||
            (flow === 'signUp' && (
              <SignUpForm
                state={oauthState.signUp}
                stateErr={oauthErrState?.signUp || {}}
                onHandler={onHandler(flow)}
                setState={(key: keyof IOauthState['signUp'], value: string) =>
                  setOauthState((prevState) => ({
                    ...prevState,
                    [flow]: {
                      ...prevState[flow],
                      [key]: value,
                    },
                  }))
                }
              />
            )) ||
            (flow === 'activate' && (
              <FormActivation
                state={oauthState.activate}
                stateErr={oauthErrState?.activate || {}}
                onHandler={onHandler(flow)}
                setState={(key: keyof IOauthState['activate'], value: string) =>
                  setOauthState((prevState) => ({
                    ...prevState,
                    [flow]: {
                      ...prevState[flow],
                      [key]: value,
                    },
                  }))
                }
              />
            ))}

          <Copyright sx={{ mt: 5 }} />
        </Box>
      </Grid>
    </Grid>
  )
}

export default OauthPage
